import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Stack,
    TextField,
    InputAdornment,
    Skeleton
} from '@mui/material';
import {
    Search as SearchIcon,
    CheckCircle as CheckIcon,
    MoreHoriz as MoreIcon,
    Warning as PriorityIcon,
    Event as CalendarIcon,
    ReportProblem as SiteErrorIcon,
    ErrorOutline as ErrorOutlineIcon,
    NotificationsActive as CanAlertIcon,
    Error as FaultIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import serviceTicketService from '../../services/serviceTicketService';
import maintenanceService from '../../services/maintenanceService';
import DailyReportPage from './DailyReportPage';
import EquipmentPage from './EquipmentPage';
import { useDeviceStore } from '../../store/DeviceStore';

// Stat-card categories addressable from the top bar (?cat=…). The Service
// department headers deep-link Equipment (TCU_MACHINES) and Job Cards
// (OPEN_TICKETS) — see utils/departments.js.
const SVC_CATS = [
    'DAILY_REPORT', 'TCU_MACHINES', 'PM_SCHEDULE', 'CAN_ALERTS', 'FAULTS', 'WARNINGS',
    'OPEN_TICKETS', 'IN_PROGRESS', 'WAITING_PARTS', 'UPCOMING_MAINTENANCE', 'MANPOWER',
    'UNDER_PDI', 'READY_FOR_RENT', 'HIGH_PRIORITY', 'RESOLVED', 'SITE_ERRORS',
];

const ServicePage = ({ forcedCat = null }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [alarms, setAlarms] = useState([]);
    // Preventive-maintenance schedule (imported MSC sheet, pm_schedule table).
    const [pmSchedule, setPmSchedule] = useState({ data: [], due: 0, overdue: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    // Card precedence: forcedCat (the Service department's own routes each pin
    // one view) → ?cat= (legacy top-bar deep link) → saved return-card (set
    // when drilling into a ticket, so coming BACK restores it) → Daily Report.
    const SVC_RETURN_KEY = 'serviceReturnCategory';
    const [searchParams] = useSearchParams();
    const [selectedCategory, setSelectedCategory] = useState(() => {
        if (forcedCat && SVC_CATS.includes(forcedCat)) return forcedCat;
        const p = searchParams.get('cat');
        if (SVC_CATS.includes(p)) return p;
        const saved = sessionStorage.getItem(SVC_RETURN_KEY);
        sessionStorage.removeItem(SVC_RETURN_KEY); // consume
        return saved || 'DAILY_REPORT';
    });
    // Top-bar headers point at cards on this same page — follow ?cat= changes
    // while mounted (navigating ?cat=A → ?cat=B does not remount the page).
    // A forcedCat route owns its view outright and ignores the query.
    useEffect(() => {
        if (forcedCat) { if (SVC_CATS.includes(forcedCat)) setSelectedCategory(forcedCat); return; }
        const p = searchParams.get('cat');
        if (p && SVC_CATS.includes(p)) setSelectedCategory(p);
    }, [searchParams, forcedCat]);
    const goDetail = (path) => { sessionStorage.setItem(SVC_RETURN_KEY, selectedCategory); navigate(path); };

    const [expandedMachines, setExpandedMachines] = useState({});
    const toggleExpandMachine = (eqId) => {
        setExpandedMachines(prev => ({ ...prev, [eqId]: !prev[eqId] }));
    };

    // The Equipment view is the full Device Management page (embedded below),
    // which reads the device store itself — this page only needs the store for
    // the serial lookup used by the alarm rows.
    const { state: deviceState } = useDeviceStore();

    // equipment_id -> serial/model lookup so alarm rows (which only carry the
    // internal equipment_id) can still show the serial number, not the id.
    const machineBySerial = useMemo(() => {
        const map = {};
        for (const d of (deviceState.devices || [])) {
            if (d.equipment_id) map[d.equipment_id] = { serial: d.serial_number || null, model: d.model_number || d.name || null };
        }
        return map;
    }, [deviceState.devices]);
    useEffect(() => {
        fetchTickets();
        fetchAlarms();
        fetchPMSchedule();
    }, []);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const response = await serviceTicketService.getAllTickets();
            if (response.success) {
                setTickets(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch service tickets:', error);
            setTickets([]);
        } finally {
            setLoading(false);
        }
    };

    // Open warnings + errors (site alarms) raised by the health/geofence checkers.
    const fetchAlarms = async () => {
        try {
            const response = await maintenanceService.getOpenAlarms();
            if (response.success) {
                setAlarms(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch site alarms:', error);
            setAlarms([]);
        }
    };

    const fetchPMSchedule = async () => {
        try {
            const response = await maintenanceService.getPMSchedule();
            if (response.success) {
                setPmSchedule({
                    data: response.data || [],
                    due: response.due || 0,
                    overdue: response.overdue || 0,
                });
            }
        } catch (error) {
            console.error('Failed to fetch PM schedule:', error);
        }
    };

    // ... (keep fetchTickets)

    const filteredTickets = useMemo(() => {
        let filtered = tickets;

        // Category Filter
        switch (selectedCategory) {
            case 'OPEN_TICKETS':
                filtered = tickets.filter(t => t.status === 'OPEN' || t.status === 'ASSIGNED');
                break;
            case 'IN_PROGRESS':
                filtered = tickets.filter(t => t.status === 'IN_PROGRESS');
                break;
            case 'WAITING_PARTS':
                filtered = tickets.filter(t => t.status === 'WAITING_SPARES');
                break;
            case 'UPCOMING_MAINTENANCE':
                filtered = tickets.filter(t => t.issue_type === 'PREVENTIVE_MAINTENANCE' || t.description?.toLowerCase().includes('maintenance'));
                break;
            case 'MANPOWER':
                filtered = tickets.filter(t => t.assigned_technician_name);
                break;
            case 'UNDER_PDI':
                filtered = tickets.filter(t => t.issue_type === 'PDI' || t.description?.includes('PDI'));
                break;
            case 'READY_FOR_RENT':
                filtered = tickets.filter(t => t.status === 'RESOLVED'); // Proxy logic
                break;
            case 'HIGH_PRIORITY':
                filtered = tickets.filter(t => t.priority === 'CRITICAL' || t.priority === 'HIGH');
                break;
            case 'RESOLVED':
                filtered = tickets.filter(t => t.status === 'RESOLVED');
                break;
            default:
                break;
        }

        // Search Filter
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(t =>
                t.id?.toString().includes(search) ||
                t.device_name?.toLowerCase().includes(search) ||
                t.device_id?.toString().toLowerCase().includes(search) ||
                t.assigned_technician_name?.toLowerCase().includes(search) ||
                t.description?.toLowerCase().includes(search)
            );
        }

        return filtered;
    }, [tickets, selectedCategory, searchTerm]);

    const filteredAlarms = useMemo(() => {
        let filtered = alarms.filter(a => {
            if (!a.equipment_id || a.equipment_id === 'unknown') return false;
            const eq = String(a.equipment_id).toUpperCase();
            if (eq.startsWith('TEST-') || eq.startsWith('TEST_') || ['BMLELC12009043', 'TEST-MTANDT', 'TEST-AUTOPEEPAL'].includes(eq)) return false;
            return true;
        });

        if (selectedCategory === 'CAN_ALERTS') {
            filtered = filtered.filter(a => a.category === 'DEVICE_ALERT');
        } else if (selectedCategory === 'FAULTS') {
            filtered = filtered.filter(a => a.severity?.toLowerCase() === 'critical' || a.category === 'DEVICE_DTC');
        } else if (selectedCategory === 'WARNINGS') {
            filtered = filtered.filter(a => a.severity?.toLowerCase() === 'warning');
        }

        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(a =>
                a.equipment_id?.toString().toLowerCase().includes(search) ||
                a.alarm_name?.toLowerCase().includes(search) ||
                a.message?.toLowerCase().includes(search) ||
                a.category?.toLowerCase().includes(search) ||
                a.severity?.toLowerCase().includes(search)
            );
        }
        return filtered;
    }, [alarms, selectedCategory, searchTerm]);

    const alarmsGroupedByMachine = useMemo(() => {
        const groups = {};
        for (const alarm of filteredAlarms) {
            if (!alarm.equipment_id || alarm.equipment_id === 'unknown') continue;
            const eqId = alarm.equipment_id;
            if (!groups[eqId]) {
                groups[eqId] = [];
            }
            groups[eqId].push(alarm);
        }
        return groups;
    }, [filteredAlarms]);

    const getSeverityChip = (severity) => {
        const sev = (severity || 'warning').toLowerCase();
        const color = sev === 'critical' ? 'error' : sev === 'info' ? 'info' : 'warning';
        return (
            <Chip
                icon={<ErrorOutlineIcon sx={{ fontSize: '12px !important' }} />}
                label={sev === 'critical' ? 'ERROR' : sev.toUpperCase()}
                size="small"
                color={color}
                sx={{ fontWeight: 800, borderRadius: '6px', fontSize: '10px' }}
            />
        );
    };

    const getStatusChip = (status) => {
        let color = 'default';
        if (status === 'OPEN') color = 'info';
        if (status === 'IN_PROGRESS') color = 'warning';
        if (status === 'RESOLVED' || status === 'CLOSED') color = 'success';
        if (status === 'WAITING_SPARES') color = 'secondary';

        return (
            <Chip
                label={status.replace('_', ' ')}
                size="small"
                color={color}
                sx={{ fontWeight: 800, borderRadius: '6px', fontSize: '10px' }}
            />
        );
    };

    const getPriorityChip = (priority) => {
        let color = 'default';
        if (priority === 'CRITICAL') color = 'error';
        if (priority === 'HIGH') color = 'warning';
        if (priority === 'MEDIUM') color = 'info';

        return (
            <Chip
                icon={<PriorityIcon sx={{ fontSize: '12px !important' }} />}
                label={priority}
                size="small"
                color={color}
                variant="outlined"
                sx={{ fontWeight: 800, borderRadius: '6px', fontSize: '10px' }}
            />
        );
    };

    return (
        <Box p={3} className="animate-fade-in">
            {/* No card strip here: the Service department headers in the top bar
                (Equipment · Daily Report · Maintenance · Errors · Job Cards)
                choose what this page shows, via ?cat=. */}

            {/* Service Daily Report — the default landing view */}
            {selectedCategory === 'DAILY_REPORT' && (
                <DailyReportPage embedded forcedTeam="service" />
            )}

            {/* Search Bar (Daily Report and Equipment bring their own) */}
            {selectedCategory !== 'DAILY_REPORT' && selectedCategory !== 'TCU_MACHINES' && (
            <Box mb={1.5}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by ticket ID, equipment, technician, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: 'white',
                            fontWeight: 600
                        }
                    }}
                />
            </Box>
            )}

            {/* Equipment in its service guise: only the INSTALLED fleet
                (machines with a TCU mapped, testing excluded), no page header
                or KPI strip — straight to search/status/site with Export
                (PDF/Excel) beside the Site filter. */}
            {selectedCategory === 'TCU_MACHINES' && (
                <EquipmentPage embedded variant="service" />
            )}

            {/* Preventive-maintenance schedule — imported service (MSC) sheet */}
            {selectedCategory === 'PM_SCHEDULE' && (
                <TableContainer component={Card} sx={{ borderRadius: '24px' }}>
                    {/* The due count — no title, the top bar already names the view. */}
                    <Box px={3} py={1.5} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                            {loading ? 'Loading…'
                                : `${pmSchedule.due + pmSchedule.overdue} machine(s) needing service — ${pmSchedule.overdue} overdue, ${pmSchedule.due} due`}
                        </Typography>
                    </Box>
                    <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>SERIAL NO.</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>MODEL</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>CURRENT LOCATION</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>SITE</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>LAST SERVICE</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>NEXT PLAN</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>CURRENT HMR</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>REMAINING</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>STATUS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pmSchedule.data
                                .filter(r => {
                                    const q = searchTerm.trim().toLowerCase();
                                    return !q || [r.serial_no, r.model, r.product, r.site, r.current_location, r.pm_id, r.next_service_plan]
                                        .some(x => String(x || '').toLowerCase().includes(q));
                                })
                                .map(r => {
                                    const st = r.status === 'OVERDUE'
                                        ? { label: 'OVERDUE', color: '#dc2626' }
                                        : r.status === 'DUE'
                                            ? { label: 'DUE', color: '#d97706' }
                                            : { label: 'SCHEDULED', color: '#16a34a' };
                                    const fmtDate = (d) => d
                                        ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                        : '—';
                                    return (
                                        <TableRow key={r.serial_no} hover sx={r.equipment_id ? { cursor: 'pointer' } : undefined}
                                            onClick={r.equipment_id ? () => goDetail(`/service-machine/${encodeURIComponent(r.equipment_id)}`) : undefined}>
                                            <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                                {r.serial_no}
                                                {r.pm_id && <Typography variant="caption" display="block" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{r.pm_id}</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700}>{r.model || '—'}</Typography>
                                                <Typography variant="caption" color="text.secondary">{r.product || ''}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{r.current_location || '—'}</Typography>
                                                {r.machine_status && (
                                                    <Typography variant="caption" color="text.secondary">{String(r.machine_status).replace(/_/g, ' ')}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 240 }} noWrap title={r.site || ''}>
                                                    {r.site || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{fmtDate(r.last_service_date)}</Typography>
                                                {r.last_service_hmr !== null && (
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                                                        @ {Number(r.last_service_hmr).toLocaleString('en-IN')} hrs
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {r.next_due_date ? fmtDate(r.next_due_date) : '—'}
                                                </Typography>
                                                {r.next_due_hmr !== null && (
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                                                        @ {Number(r.next_due_hmr).toLocaleString('en-IN')} hrs
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {r.current_hmr !== null ? `${Number(r.current_hmr).toLocaleString('en-IN')} hrs` : '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight={700}
                                                    sx={{ color: (r.remaining_hmr !== null && Number(r.remaining_hmr) < 0) ? '#dc2626' : 'text.primary' }}>
                                                    {r.remaining_hmr !== null ? `${Number(r.remaining_hmr).toLocaleString('en-IN')} hrs` : '—'}
                                                </Typography>
                                                <Typography variant="caption"
                                                    sx={{ color: (r.remaining_days !== null && r.remaining_days < 0) ? '#dc2626' : 'text.secondary' }}>
                                                    {r.remaining_days !== null ? `${r.remaining_days} days` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={st.label} size="small"
                                                    sx={{ bgcolor: `${st.color}15`, color: st.color, fontWeight: 800, borderRadius: '6px', fontSize: '10px' }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {pmSchedule.data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        No maintenance schedule loaded yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </Box>
                </TableContainer>
            )}

            {/* Site Errors Table — shown when the Site Errors, CAN Alerts, Faults, or Warnings box is selected */}
            {selectedCategory === 'DAILY_REPORT' || selectedCategory === 'TCU_MACHINES' || selectedCategory === 'PM_SCHEDULE' ? null :
             (selectedCategory === 'SITE_ERRORS' || selectedCategory === 'CAN_ALERTS' || selectedCategory === 'FAULTS' || selectedCategory === 'WARNINGS') ? (
            <TableContainer component={Card} sx={{ borderRadius: '24px' }}>
                <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedCategory === 'CAN_ALERTS' ? <CanAlertIcon sx={{ color: '#ea580c' }} /> :
                     selectedCategory === 'FAULTS' ? <FaultIcon sx={{ color: '#be123c' }} /> :
                     selectedCategory === 'WARNINGS' ? <PriorityIcon sx={{ color: '#eab308' }} /> :
                     <SiteErrorIcon sx={{ color: '#dc2626' }} />}
                    <Box>
                        <Typography variant="h6" fontWeight={800}>
                            {selectedCategory === 'CAN_ALERTS' ? 'CAN Alerts' :
                             selectedCategory === 'FAULTS' ? 'Faults' :
                             selectedCategory === 'WARNINGS' ? 'Warnings' :
                             'Site Warnings & Errors'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {selectedCategory === 'CAN_ALERTS' ? 'Active CAN alerts from the telematics system' :
                             selectedCategory === 'FAULTS' ? 'Critical faults and errors across the fleet' :
                             selectedCategory === 'WARNINGS' ? 'Open system warnings and advisory notices' :
                             'Open machine, system & geofence alarms across the fleet'}
                        </Typography>
                    </Box>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>MACHINE</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>ACTIVE ALARMS</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>MAX SEVERITY</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>LAST ALARM TIME</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>DETAILS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? [...Array(5)].map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={5}><Skeleton height={50} /></TableCell></TableRow>
                        )) : Object.keys(alarmsGroupedByMachine).length > 0 ? Object.entries(alarmsGroupedByMachine).map(([eqId, machineAlarms]) => {
                            const mch = machineBySerial[eqId] || {};
                            const isExpanded = !!expandedMachines[eqId];
                            const totalCount = machineAlarms.length;
                            
                            // Severity calculation
                            const hasError = machineAlarms.some(a => a.severity?.toLowerCase() === 'critical');
                            const hasWarning = machineAlarms.some(a => a.severity?.toLowerCase() === 'warning');
                            const maxSeverity = hasError ? 'critical' : hasWarning ? 'warning' : 'info';
                            
                            // Latest alarm
                            const latestAlarm = machineAlarms.reduce((latest, a) => 
                                !latest || new Date(a.created_at) > new Date(latest.created_at) ? a : latest, null
                            );

                            return (
                                <React.Fragment key={eqId}>
                                    <TableRow hover onClick={() => toggleExpandMachine(eqId)} sx={{ cursor: 'pointer', bgcolor: isExpanded ? '#f8fafc' : 'inherit' }}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{mch.serial || eqId}</Typography>
                                            {mch.model && <Typography variant="caption" color="text.disabled">{mch.model}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={`${totalCount} ${totalCount === 1 ? 'Alarm' : 'Alarms'}`} 
                                                size="small" 
                                                sx={{ fontWeight: 800, bgcolor: '#f1f5f9', color: '#475569', borderRadius: '6px' }} 
                                            />
                                        </TableCell>
                                        <TableCell>{getSeverityChip(maxSeverity)}</TableCell>
                                        <TableCell>
                                            {latestAlarm?.created_at ? (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                        {new Date(latestAlarm.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                    </Typography>
                                                </Stack>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small">
                                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Expanded details row */}
                                    {isExpanded && (
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            <TableCell colSpan={5} sx={{ p: 2, pt: 0 }}>
                                                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', bgcolor: 'white', overflow: 'hidden', my: 1 }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', py: 1 }}>ALARM NAME</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', py: 1 }}>MESSAGE</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', py: 1 }}>CATEGORY</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', py: 1 }}>SEVERITY</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', py: 1 }}>RAISED</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {machineAlarms.map((alarm) => (
                                                                <TableRow key={alarm.id} hover>
                                                                    <TableCell sx={{ py: 1, fontWeight: 700, fontSize: '12px' }}>{alarm.alarm_name || 'Alarm'}</TableCell>
                                                                    <TableCell sx={{ py: 1, fontSize: '12px', color: 'text.secondary' }}>{alarm.message || '—'}</TableCell>
                                                                    <TableCell sx={{ py: 1 }}>
                                                                        <Chip label={(alarm.category || '').replace('_', ' ') || '—'} size="small" variant="outlined" sx={{ fontWeight: 700, borderRadius: '4px', fontSize: '9px', height: 18 }} />
                                                                    </TableCell>
                                                                    <TableCell sx={{ py: 1 }}>{getSeverityChip(alarm.severity)}</TableCell>
                                                                    <TableCell sx={{ py: 1 }}>
                                                                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '11px' }}>
                                                                            {alarm.created_at ? new Date(alarm.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        }) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                        <CheckIcon sx={{ color: '#10b981', fontSize: 40 }} />
                                        <Typography variant="body2" color="text.secondary">No active warnings or errors</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            ) : (
            /* Tickets Table */
            <TableContainer component={Card} sx={{ borderRadius: '24px' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>TICKET ID</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>EQUIPMENT</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>ISSUE TYPE</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>PRIORITY</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>STATUS</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>TECHNICIAN</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>OPENED</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px', color: 'text.secondary' }}>ACTION</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? [...Array(5)].map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={8}><Skeleton height={50} /></TableCell></TableRow>
                        )) : filteredTickets.length > 0 ? filteredTickets.map((ticket) => (
                            <TableRow key={ticket.id} hover sx={{ cursor: 'pointer' }} onClick={() => goDetail(`/service/tickets/${ticket.id}`)}>
                                <TableCell sx={{ fontWeight: 800, color: 'primary.main', fontFamily: 'monospace' }}>#{ticket.id}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{ticket.device_name || ticket.device_id}</Typography>
                                    <Typography variant="caption" color="text.secondary">{ticket.device_id}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ticket.issue_type?.replace('_', ' ')}</Typography>
                                </TableCell>
                                <TableCell>{getPriorityChip(ticket.priority)}</TableCell>
                                <TableCell>{getStatusChip(ticket.status)}</TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {ticket.assigned_technician_name ? (
                                            <>
                                                <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.100', color: 'primary.main', fontSize: '10px', fontWeight: 900 }}>
                                                    {ticket.assigned_technician_name.charAt(0)}
                                                </Avatar>
                                                <Typography variant="caption" fontWeight={700}>{ticket.assigned_technician_name}</Typography>
                                            </>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled" fontStyle="italic">Unassigned</Typography>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                                            {new Date(ticket.opened_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small"><MoreIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                    <Typography variant="body2" color="text.secondary">No tickets match your criteria</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            )}
        </Box>
    );
};

export default ServicePage;
